'use client';
import React, { useState, useEffect } from 'react';
import party from 'party-js';
import { motion, AnimatePresence } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';

interface FormErrors {
  name?: string;
  email?: string;
  mobile?: string;
  message?: string;
  honeypot?: string;
  submit?: string;
}

export const EnquiryForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [clientIp, setClientIp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
    honeypot: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const siteKey = "0x4AAAAAABhnoWzkfK2Wo_Wz"; // Use your Cloudflare Turnstile sitekey

  // Fetch client IP
// Replace the entire useEffect that fetches IP
useEffect(() => {
  const fetchIp = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setClientIp(data.ip || 'unknown');
    } catch (error) {
      console.warn('IP fetch blocked by CSP or failed:', error);
      setClientIp('unknown'); // safe fallback
    }
  };
  fetchIp();
}, []);


  // Form validation
  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) newErrors.email = 'Valid email is required';
    if (!formData.mobile.match(/^\d{10}$/)) newErrors.mobile = 'Valid 10-digit mobile number is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.honeypot) newErrors.honeypot = 'Bot detected';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Input sanitization
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = value
      .replace(/[<>{}]/g, '')
      .replace(/script/gi, '')
      .slice(0, name === 'message' ? 500 : 100);
    setFormData({ ...formData, [name]: sanitizedValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (formData.honeypot) return;

    setIsLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", "Basic VmpGaW5hbmNlMTM1Mjc1OllSRUZWS0hEUFNHT1NHUzUyNVNHS0RLR0Y5NQ==");

      const clientInfo = {
        browser: navigator.userAgent,
        os: navigator.platform,
        device: (() => {
          const ua = navigator.userAgent;
          if (/mobile/i.test(ua)) return 'Mobile';
          if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
          if (/android/i.test(ua) && !/mobile/i.test(ua)) return 'Tablet';
          if (/Macintosh/i.test(ua) && 'ontouchend' in document) return 'Tablet';
          if (/Windows NT/i.test(ua) && /Touch/i.test(ua)) return 'Tablet';
          return 'Desktop';
        })(),
        ip: clientIp,
      };

      const requestBody = {
        form_type: 2,
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        message: formData.message,
        client_info: JSON.stringify(clientInfo),
      };

      const response = await fetch('https://clientconnect.vilvabusiness.com/api/vijayfinance/submit-form-request', {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setIsOpen(false);
        setIsSubmitted(true);
        party.confetti(document.body, {
          count: party.variation.range(50, 100),
          size: party.variation.range(0.8, 1.2),
        });
        setFormData({ name: '', email: '', mobile: '', message: '', honeypot: '' });
        setErrors({});
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setErrors({ submit: 'Submission failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-close success message after 5 seconds
  useEffect(() => {
    if (isSubmitted) {
      const timer = setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isSubmitted]);

  return (
    <>
      <AnimatePresence>
        {isOpen && !isSubmitted && (
          <motion.div
            initial={{ y: '100vh', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100vh', opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed right-5 bottom-20 z-50"
          >
            <div className="bg-white shadow-2xl rounded-xl p-6 w-80 sm:w-96 border border-gray-100">
              <h2 className="text-xl font-bold font-sans mb-5 text-gray-900">Enquiry Form</h2>
              <form onSubmit={handleSubmit} noValidate>
                <div className="space-y-5">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full rounded-[4px] border border-[#9B9B9B] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

                      aria-label="Name"
                      required
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-[4px] border border-[#9B9B9B] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

                      aria-label="Email"
                      required
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="mobile"
                      placeholder="Your Mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="w-full rounded-[4px] border border-[#9B9B9B] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Mobile"
                      required
                    />
                    {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full h-20 rounded-[4px] border border-[#9B9B9B] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    ></textarea>
                    {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                  </div>
                  <div className="absolute left-[-9999px]">
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  <div className='flex justify-center'>
                    <Turnstile
                      siteKey={siteKey}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken('')}
                    />
                  </div>

                  {errors.submit && <p className="text-red-500 text-xs mt-2">{errors.submit}</p>}
                  <button
                    type="submit"
                    disabled={!captchaToken || isLoading}
                    className={`w-full p-3 rounded-lg !font-medium transition-colors flex items-center justify-center
          ${isLoading || !captchaToken
                        ? '!bg-gray-300 text-gray-500 cursor-not-allowed'
                        : '!bg-blue-600 hover:bg-gray-700 text-white'}`}
                  >
                    {isLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : null}
                    {isLoading ? 'Submitting...' : 'Submit Enquiry'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isSubmitted && (
          <motion.div
            initial={{ y: '100vh', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100vh', opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed right-5 bottom-20 z-50"
          >
            <div className="bg-white shadow-2xl rounded-xl p-6 w-80 sm:w-96 border border-gray-100 text-center">
              <h2 className="text-xl font-bold font-sans mb-4 text-gray-900">Thank You!</h2>
              <p className="text-gray-600 text-sm">Your enquiry has been submitted successfully. We'll get back to you soon!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className="!bg-[#164686] bottom-5 right-5 fixed p-3 rounded-full cursor-pointer hover:!bg-blue-700 transition-colors z-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <svg
  className={`w-6 h-6 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-90' : 'rotate-45'} text-white`}
  fill="none"
  viewBox="0 0 24 24"
  aria-label="Close form"
>
  <path
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M6 18L18 6M6 6L18 18"
  />
</svg>


        ) : (
          <svg
className={`w-6 h-6 transition-transform duration-300 ease-in-out ${!isOpen ? 'rotate-95' : 'rotate-90'} text-white`}
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-label="Open enquiry form"
          >
            <path
              d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
            />
          </svg>
        )}
      </div>
    </>
  );
};